import React from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { 
  Plus, 
  ArrowRight, 
  Flag, 
  Layers,
  Maximize2,
  ZoomIn, 
  ZoomOut,
  MousePointer2,
  Undo2,
  Redo2
} from 'lucide-react';

/**
 * Graph Toolbar - Desktop-Grade Persistent Toolbar
 * 
 * This is NOT a floating mobile FAB.
 * This is part of the center surface chrome.
 * 
 * Tools:
 * - Undo/Redo
 * - Add State
 * - Add Transition (manual mode)
 * - Mark Initial
 * - Group/Ungroup (compound states - placeholder)
 * - Fit to View
 * - Toggle Focus Mode
 */
const GraphToolbar = ({ 
  onAddState, 
  onAddTransition, 
  onMarkInitial,
  onGroup,
  onZoomIn,
  onZoomOut,
  onFitView,
  onFocusMode,
  mode = 'select',
  onModeChange,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  undoDescription,
  redoDescription
}) => {
  const tools = [
    { 
      id: 'select', 
      label: 'Select', 
      shortcut: 'V',
      icon: MousePointer2, 
      action: () => onModeChange?.('select'),
      separator: false
    },
    { 
      id: 'addState', 
      label: 'Add State', 
      shortcut: 'S',
      icon: Plus, 
      action: onAddState,
      separator: false
    },
    { 
      id: 'addTransition', 
      label: 'Add Transition', 
      shortcut: 'T',
      icon: ArrowRight, 
      action: onAddTransition,
      separator: false
    },
    { 
      id: 'markInitial', 
      label: 'Mark Initial', 
      shortcut: 'I',
      icon: Flag, 
      action: onMarkInitial,
      separator: true
    },
    { 
      id: 'group', 
      label: 'Group (Compound)', 
      shortcut: 'G',
      icon: Layers, 
      action: onGroup,
      disabled: true, // Placeholder for next phase
      separator: true
    },
  ];

  const viewTools = [
    { id: 'zoomIn', label: 'Zoom In', shortcut: '+', icon: ZoomIn, action: onZoomIn },
    { id: 'zoomOut', label: 'Zoom Out', shortcut: '-', icon: ZoomOut, action: onZoomOut },
    { id: 'fitView', label: 'Fit to View', shortcut: '0', icon: Maximize2, action: onFitView },
  ];

  return (
    <TooltipProvider>
      <div 
        className="absolute top-3 left-3 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg p-1.5 flex items-center gap-1 z-20 shadow-lg"
        data-testid="graph-toolbar-desktop"
      >
        {/* Undo/Redo */}
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onUndo}
                disabled={!canUndo}
                className={cn(
                  "h-8 w-8 p-0",
                  !canUndo && "opacity-40 cursor-not-allowed"
                )}
                data-testid="tool-undo"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              <p>Undo <span className="text-gray-400">(⌘Z)</span></p>
              {undoDescription && <p className="text-gray-500">{undoDescription}</p>}
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onRedo}
                disabled={!canRedo}
                className={cn(
                  "h-8 w-8 p-0",
                  !canRedo && "opacity-40 cursor-not-allowed"
                )}
                data-testid="tool-redo"
              >
                <Redo2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              <p>Redo <span className="text-gray-400">(⌘⇧Z)</span></p>
              {redoDescription && <p className="text-gray-500">{redoDescription}</p>}
            </TooltipContent>
          </Tooltip>
        </div>
        
        <div className="w-px h-5 bg-white/10 mx-1" />

        {/* Primary Tools */}
        <div className="flex items-center gap-0.5">
          {tools.map((tool, idx) => {
            const Icon = tool.icon;
            const isActive = mode === tool.id;
            
            return (
              <React.Fragment key={tool.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant={isActive ? "default" : "ghost"}
                      onClick={() => {
                        if (!tool.disabled) {
                          onModeChange?.(tool.id);
                          tool.action?.();
                        }
                      }}
                      disabled={tool.disabled}
                      className={cn(
                        "h-8 w-8 p-0",
                        isActive && "bg-blue-500/30 text-blue-300",
                        tool.disabled && "opacity-40 cursor-not-allowed"
                      )}
                      data-testid={`tool-${tool.id}`}
                    >
                      <Icon className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    <p>{tool.label} <span className="text-gray-400">({tool.shortcut})</span></p>
                    {tool.disabled && <p className="text-yellow-400">Coming soon</p>}
                  </TooltipContent>
                </Tooltip>
                {tool.separator && (
                  <div className="w-px h-5 bg-white/10 mx-1" />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* View Tools */}
        <div className="flex items-center gap-0.5">
          {viewTools.map((tool) => {
            const Icon = tool.icon;
            return (
              <Tooltip key={tool.id}>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={tool.action}
                    className="h-8 w-8 p-0"
                    data-testid={`tool-${tool.id}`}
                  >
                    <Icon className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <p>{tool.label} <span className="text-gray-400">({tool.shortcut})</span></p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default GraphToolbar;
